import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { APP } from '@/constants'
import { useHead, useSeoMeta } from '@unhead/vue'
import type { ReactiveHead } from '@unhead/vue'

export function useSeoI18n() {
  const { t, locale, availableLocales } = useI18n()
  const route = useRoute()

  useHead((): ReactiveHead => {
    let title = `${APP.DEFAULT_TITLE}`
    if (route.meta?.titleKey) {
      title = `${t(route.meta.titleKey as string)} - ${APP.DEFAULT_TITLE}`
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const path = route.path

    return {
      title,
      link: [
        // Canonical: avoids duplicate content issues from ?lang= variants
        {
          rel: 'canonical',
          href: `${origin}${path}`,
        },
        // One <link> per supported locale
        ...availableLocales.map((lang) => ({
          rel: 'alternate' as const,
          hreflang: lang,
          href: `${origin}${path}?lang=${lang}`,
        })),
        // x-default points to the canonical URL without a lang param
        {
          rel: 'alternate' as const,
          hreflang: 'x-default',
          href: `${origin}${path}`,
        },
      ],
    }
  })

  useSeoMeta({
    description: computed(() => t('seo.description')),
    ogDescription: computed(() => t('seo.description')),
    ogLocale: computed(() => locale.value),
  })
}
